<template>
  <div>
    <vx-card class="">
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-5">
      <heading size="h2" style-size="h4" class="mb-5">Let’s start your Nursing experience!</heading>
      <p class="mb-5">
        There are 2 types of nurses that have two different sets of educational requirements.
      </p>

      <heading size="h3" style-size="h5" class="mb-5">1. Enrolled Nurse</heading>
      <p class="mb-3">
        <u><strong>Description</strong></u>
      </p>
      <p class="mb-5">
        Enrolled Nurses provide nursing care to patients in a variety of health, aged care, welfare
        and community settings and hospitals under the supervision of Registered Nurses.
      </p>
      <p class="mb-3">
        <u><strong>Education Level</strong></u>
      </p>
      <p class="mb-5">
        You usually need a diploma or advanced diploma in nursing and clinical experience to work as
        an Enrolled Nurse.
      </p>
      <vs-divider />
      <heading size="h3" style-size="h5" class="mb-5">1. Registered Nurse</heading>
      <p class="mb-3">
        <u><strong>Description</strong></u>
      </p>
      <p class="mb-5">
        Registered Nurses provide nursing care to patients in hospitals, clinics, aged care and
        other health care facilities, and in the community.
      </p>
      <p class="mb-3">
        <u><strong>Education Level</strong></u>
      </p>
      <p class="mb-5">
        You usually need a bachelor’s degree in nursing to work as a Registered Nurse.
      </p>
      <vs-divider />

      <p class="mb-5">Click on the below icon to see the different types of Registered Nurses:</p>

      <p class="mb-5">
        There are many other specialised positions and roles with higher pay rates and/or different
        career areas that being a registered nurse can lead to. You can explore these online at
        places like:
        <a href="https://joboutlook.gov.au/careers/explore-careers" target="_blank"
          >https://joboutlook.gov.au/careers/explore-careers</a
        >
      </p>

      <button
        class="btn btn-secondary btn-size-medium includedIcon"
        ref="nurseTypesBtn"
        @click="popupActive = true"
      >
        <list-icon />See types of registered nurses
      </button>

      <generic-popup title="Nurse Types" v-if="popupActive" @close="closeTextPopup">
        <p class="mb-3">
          There are many different types of nursing that Registered Nurses can get into.
        </p>
        <p class="mb-3">Some include:</p>
        <heading size="h2" style-size="h5" class="mb-3">Aged Care</heading>
        <p class="mb-3">
          Registered Nurses (Aged Care) provide nursing care to the elderly in community settings,
          residential aged care facilities, retirement villages and health care facilities.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Child and Family Health</heading>
        <p class="mb-3">
          Registered Nurses (Child and Family Health) provide nursing care to children and their
          families, with an emphasis on the prevention, early detection of, and early intervention
          in, physical, emotional and social problems affecting children and their families.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Community Health</heading>
        <p class="mb-3">
          Registered Nurses (Community Health) provide nursing care, health counselling, screening
          and education to individuals, families and groups in the wider community with a focus on
          patient independence and health promotion.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Critical Care and Emergency</heading>
        <p class="mb-3">
          Registered Nurses (Critical Care and Emergency) provide nursing care to critically ill
          patients and patients with unstable health following injury, surgery or during the acute
          phase of diseases, integrating new technological equipment into care in settings such as
          high dependency units, intensive care units, emergency departments or retrieval services.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Developmental Disability</heading>
        <p class="mb-3">
          Registered Nurses (Developmental Disability) provide nursing care to people with
          intellectual and development disabilities in a range of health, welfare and community
          settings.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Disability and Rehabilitation</heading>
        <p class="mb-3">
          Registered Nurses (Disability and Rehabilitation) provide nursing care to patients
          recovering from injury and illness, and assist and facilitate patients with disabilities
          to live more independently.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Medical</heading>
        <p class="mb-3">
          Registered Nurses (Medical) provide nursing care to patients with conditions such as
          infections, metabolic disorders and degenerative conditions, which require medical
          intervention in a range of health, aged care and community settings.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Medical Practice</heading>
        <p class="mb-3">
          Registered Nurses (Medical Practice) provide clinical care to patients, undertake clinical
          organisation and practice administration, and facilitate communication within a general
          practice environment and between the practice and outside organisations and individuals.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Mental Health</heading>
        <p class="mb-3">
          Registered Nurses (Mental Health) provide nursing care to patients with mental health
          illness, disorder and dysfunction, and those experiencing emotional difficulties, distress
          and crisis in health, welfare and aged care facilities, correctional services and the
          community.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Perioperative</heading>
        <p class="mb-3">
          Registered Nurses (Perioperative) provide nursing care to patients before, during and
          immediately after surgery, assess patients' condition, plan nursing care for surgical
          intervention, maintain a safe and comfortable environment, assist Surgeons and
          Anaesthetists during surgery, and monitor patients' recovery from anaesthetic, prior to
          return to, or discharge from, ward.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Surgical</heading>
        <p class="mb-3">
          Registered Nurses (Surgical) provide nursing care to patients with injuries and illness
          that require surgical intervention.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Paediatrics</heading>
        <p class="mb-3">
          Registered Nurses (Paediatrics) provide nursing care to children of all ages, including
          neonates (newborn), across a range of health and community settings with an emphasis on
          assessing children’s acute and ongoing needs while taking into account physical, social,
          cultural and family circumstances.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Other</heading>
        <p class="mb-3">
          Registered Nurses (not covered elsewhere) includes jobs like Flight Nurse, Nursing Officer
          (Defence Forces), Registered Nurse (Infection Control), and Registered Nurse (Remote or
          Rural Area).
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Aesthetic (Cosmetic)</heading>
        <p class="mb-3">
          Cosmetic nurses perform a range of non-surgical procedures to assist patients with their
          appearance. Procedures include:
        </p>
        <heading size="h3" style-size="h5" class="mb-3 font-normal"><u>Injections</u></heading>
        <ul class="default-list">
          <li>Botox</li>
          <li>Collagen/other fillers</li>
          <li>Neurotoxin</li>
          <li>Sclerotherapy</li>
        </ul>

        <heading size="h3" style-size="h5" class="mb-3 font-normal"><u>Skin treatments</u></heading>

        <ul class="default-list">
          <li>Microdermabrasion</li>
          <li>Tattoo removal</li>
          <li>Chemical peels</li>
          <li>Light therapy</li>
          <li>Micro-needling</li>
        </ul>

        <heading size="h3" style-size="h5" class="mb-3 font-normal"
          ><u>Laser treatments</u></heading
        >

        <ul class="default-list">
          <li>Hair removal</li>
          <li>Skin rejuvenation</li>
        </ul>

        <p class="mb-3">
          <u>Note:</u> To become a cosmetic nurse, registered nurses must complete a postgraduate
          qualification in cosmetic nursing, such as a graduate diploma.
        </p>
        <heading size="h2" style-size="h5" class="mb-3">Midwives</heading>
        <heading size="h3" style-size="h5" class="mb-3 font-normal text-underline_italic"
          >Description</heading
        >
        <p class="mb-3">
          Midwives provide care and advice to women during pregnancy, labour and childbirth, and
          postnatal care for women and babies in a range of settings such as the home, community,
          hospitals, clinics and health units.
        </p>
        <heading size="h3" style-size="h5" class="mb-3 font-normal text-underline_italic"
          >Education Level</heading
        >
        <p class="mb-3">
          You need a bachelor’s degree in midwifery or a bachelor’s in nursing followed by a
          midwifery postgraduate degree to work as a Midwife.
        </p>
      </generic-popup>
    </vx-card>

    <vx-card>
      <heading size="h2" style-size="h4" class="mb-5">TASKS – Registered Nurse</heading>
      <p class="mb-5">
        (Click the down arrows on the right to get descriptions and examples of each one)
      </p>
      <accordion-panel
        aria-title="Assessing, planning, implementing and evaluating nursing care for patients according to
                  accepted nursing practice and standards."
      >
        <template v-slot:title>
          <div>
            Assessing, planning, implementing and evaluating nursing care for patients according to
            accepted nursing practice and standards.
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> working within your scope of practice (what you are allowed
            to do by law) and using the most up to date information when planning and delivering
            care to patients.
          </p>
          <p>
            <strong>An example</strong> of this could be by taking on a new patient who has a bad
            infection. You would take your vital signs (assessment), give the results to the doctor
            and create a plan together, such as giving some antibiotics. Then you would continue to
            check the patients vital signs to see if your plan is helping the patient to get better.
          </p>
        </template>
      </accordion-panel>
      <accordion-panel
        aria-title="Working in consultation with other Health Professionals and members of health teams, and
                  coordinating the care of patients."
      >
        <template v-slot:title>
          <div>
            Working in consultation with other Health Professionals and members of health teams, and
            coordinating the care of patients.
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> identifying what the patient needs to get better, and making
            sure that what you are doing is working.
          </p>
          <p>
            <strong>An example</strong> of this might be looking after someone who has been in a car
            accident and has broken their back. As the nurse you might talk to the physiotherapist
            to help the patient become strong again, and you might talk to the occupational
            therapist to see what sort of equipment the patient would need to move around safely,
            like a walking frame.
          </p>
        </template>
      </accordion-panel>
      <accordion-panel
        aria-title=" Providing interventions, treatments and therapies such as medications, and monitoring
                  responses to treatment and care plan."
      >
        <template v-slot:title>
          <div>
            Providing interventions, treatments and therapies such as medications, and monitoring
            responses to treatment and care plan.
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> working together with doctors, physiotherapists,
            occupational therapists and other health care specialists to provide comprehensive care
            to patients.
          </p>
          <p>
            <strong>An example</strong> might be taking a patients vital signs, but find that their
            oxygen levels are low. You understand that the patient needs to have more oxygen to help
            breathe more easily, so (with the doctors permission) you give them some more, and check
            again in a little while to see if their oxygen levels have improved.
          </p>
        </template>
      </accordion-panel>
      <accordion-panel
        aria-title="Promoting health and assisting in preventing ill health by participating in health
                  education and other health promotion activities."
      >
        <template v-slot:title>
          <div>
            Promoting health and assisting in preventing ill health by participating in health
            education and other health promotion activities.
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> helping to improve people’s health in general, and reduce
            their chances of having to come into hospital because they are unwell. This is
            especially important when managing a lifelong illness (also known as chronic illness).
          </p>
          <p>
            <strong>An example</strong> of this could be connecting a patient with community
            diabetes clinic and teaching them how to give themselves an insulin injection so they
            can manage their diabetes at home.
          </p>
        </template>
      </accordion-panel>
      <accordion-panel
        aria-title="   Answering questions and providing information to patients and families about treatment
                  and care."
      >
        <template v-slot:title>
          <div>
            Answering questions and providing information to patients and families about treatment
            and care.
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> you help patients understand their condition, their care and
            what treatments they are able access. This is called making the patients partners in
            their care.
          </p>
          <p>
            <strong>For example,</strong> a patient who is newly diagnosed with cancer. As the nurse
            you can provide them with information about their condition using fact sheets and other
            documents developed by your organisation. You can also provide them with information for
            continued support, such as community health, support groups and helping them to get in
            touch with specialist medical professionals.
          </p>
        </template>
      </accordion-panel>
      <accordion-panel
        aria-title="Supervising and coordinating the work of Enrolled Nurses and other health care workers"
      >
        <template v-slot:title>
          <div>
            Supervising and coordinating the work of Enrolled Nurses and other health care workers
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> making sure that everyone in the nursing team is working
            within their scope of practice and is providing safe and effective care to their
            patients. This means you have to know and understand what each type of nurse and health
            care assistant is allowed to do.
          </p>
          <p>
            <strong>An example</strong> of this might be working in a team of nurses in a hospital.
            You assess what type of nurses and nursing assistants you have in your team, and then
            see what tasks need to be completed for each patient that shift. Using your knowledge,
            you give the right task to the right worker (this is called delegation). Then you check
            in regularly with your team to make sure the tasks are getting completed and the patient
            is receiving the best care possible.
          </p>
        </template>
      </accordion-panel>
    </vx-card>

    <vx-card>
      <heading size="h2" style-size="h4" class="mb-5">TASKS – Enrolled Nurse</heading>
      <p class="mb-5">
        (Click the down arrows on the right to get descriptions and examples of each one)
      </p>
      <accordion-panel
        aria-title="Assesses, plans and implements nursing care for patients according to accepted nursing
                  practice and standards."
      >
        <template v-slot:title>
          <div>
            Assesses, plans and implements nursing care for patients according to accepted nursing
            practice and standards.
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">As per Registered Nurse example.</p>
        </template>
      </accordion-panel>
      <accordion-panel
        aria-title="Provides interventions, treatments and therapies such as administering medications, and
                  monitoring responses to treatments and care plans."
      >
        <template v-slot:title>
          <div>
            Provides interventions, treatments and therapies such as administering medications, and
            monitoring responses to treatments and care plans.
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">As per Registered Nurse example.</p>
          <p>
            Enrolled nurses can do almost everything an RN (registered nurse) can do, but they must
            work under the supervision of an RN, and typically cannot become specialist
          </p>
        </template>
      </accordion-panel>
    </vx-card>

    <vx-card title="Male and Female Nurses">
      <p class="mb-5">
        As you can see in the table below, in Australia our male registered nurses only make up
        about 11% of our nursing workforce. It is a similar situation with enrolled nurses.
      </p>
      <p class="mb-5">
        However, there is a push for change and a <strong>need for both</strong> male and female
        nurses.
      </p>

      <div class="vx-row mt-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/4 lg:w-1/3 w-full my-5">
          <heading size="h2" style-size="h4" class="text-center">Registered Nurses</heading>
          <img
            src="@/assets/images/experiences/nurse/jo1.png"
            class="mx-auto img-responsive"
            alt="Registered Nurses job outlook: $1,909 weekly pay, strong future growth, 280,100 workers, very high skill level, 50% full-time, 41 hours average full-time week, average age 43, 89% female."
          />
        </div>
        <div class="vx-col xl:w-1/4 lg:w-1/3 w-full my-5">
          <heading size="h3" style-size="h5" class="text-center">Enrolled Nurses</heading>
          <img
            src="@/assets/images/experiences/nurse/jo2.png"
            alt="Weekly pay: $1426. Future growth: strong. Employment size: 34000 workers. Skill level rating: high skill. Full-time share: 43% full-time. Average full time: 42 hours. Average age: 46 years. Gender share: 90% female."
            class="mx-auto img-responsive"
          />
        </div>
      </div>
      <p class="mb-10 text-center">
        Sourced from <a href="https://www.joboutlook.gov.au/" target="_blank">Job Outlook</a>
      </p>

      <vs-alert color="dark" title="Caution" active="true" class="mb-5">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
      <p class="mb-5">
        Whether you are a male or female student, please watch the below video titled “Push to boost
        the number of male nurses.”
      </p>

      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <label>
            <video-trigger
              :imageSrc="video.image"
              button_id="video-btn-male-nurses"
              @click="showYoutubePopup(video.url, `video-btn-male-nurses`)"
              :ariaLabel="video.alt"
            />
            <p>
              <small>
                Please click on the image to view the video. If your school has not unblocked
                Youtube, please view either at home or on your phone.
              </small>
            </p>
          </label>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import store from '@/store/store';
import VideoTrigger from '@/components/UI/VideoTrigger';
import AccordionPanel from '@/components/AccordionPanel';
import GenericPopup from '@/components/UI/GenericPopup';
import { ListIcon } from 'vue-feather-icons';

export default {
  components: { AccordionPanel, VideoTrigger, GenericPopup, ListIcon },
  data() {
    return {
      image: require('@/assets/images/experiences/experience-one.png'),
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      showVideoPopup: false,
      videoTriggerImg: require('@/assets/images/experiences/nurse/1. Male Nurse.png'),
      video: {
        url: 'https://www.youtube.com/embed/kvrJ6OwVexE?autoplay=1',
        image: require('@/assets/images/experiences/nurse/1. Male Nurse.png'),
        alt: 'Push to boost the number of male nurses',
      },
    };
  },
  provide: function () {
    return {
      openPopup: this.openPopup,
      closePopup: this.closePopup,
    };
  },
  methods: {
    closeTextPopup() {
      this.popupActive = false;
      this.$refs.nurseTypesBtn.focus();
    },
    openPopup() {
      this.showVideoPopup = true;
    },
    closePopup() {
      this.showVideoPopup = false;
    },
    showYoutubePopup(url, elemId) {
      store.dispatch('popup/showIframePopup', url);
      store.commit('popup/setFocusElemOnPopupClose', elemId);
    },
  },
};
</script>
